import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth, useAuthCheck } from '@/hooks/auth';
import { buildSigninPath } from '@/utils/return-to';
import { AuthContext, type AuthContextType } from './auth-context';

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { data } = useAuthCheck();
  const [authInfo, setAuthInfo] = useState<AuthContextType>({
    isAuthenticated: data.isAuthenticated ?? false,
    isAdmin: data.user?.role === 'ADMIN',
    orgId: data.user?.organization?.id ?? 0, // 無効なIDの場合は 0 を設定しておく.(バックエンドでも無効な値)
    featureFlags:
      data.user?.organization?.feature_flags?.map((f) => ({
        featureType: f.feature_type,
        isEnabled: f.is_enabled,
      })) ?? [],
    menuVisibilitySettings:
      data.user?.organization?.menu_visibility_settings?.map((s) => ({
        menuType: s.menu_type,
        isHidden: s.is_hidden,
      })) ?? [],
  });

  useEffect(() => {
    setAuthInfo({
      isAuthenticated: data.isAuthenticated ?? false,
      isAdmin: data.user?.role === 'ADMIN',
      orgId: data.user?.organization?.id ?? 0,
      featureFlags:
        data.user?.organization?.feature_flags?.map((f) => ({
          featureType: f.feature_type,
          isEnabled: f.is_enabled,
        })) ?? [],
      menuVisibilitySettings:
        data.user?.organization?.menu_visibility_settings?.map((s) => ({
          menuType: s.menu_type,
          isHidden: s.is_hidden,
        })) ?? [],
    });
  }, [data]);

  return (
    <AuthContext.Provider
      value={{
        ...authInfo,
        setAuthInfo,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

interface ProtectedRouteProps {
  children: ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    // 通知メールのリンク(?link_to_id= 等)を踏んで未ログインだった場合にクエリを失わないよう
    // search も含めて戻り先に引き継ぐ
    return <Navigate to={buildSigninPath(`${location.pathname}${location.search}`)} replace />;
  }

  return <>{children}</>;
};
